import { useEffect, useMemo, useState } from 'react';
import { ROUTES, SystemConstants } from '../../constants/constants';
import { useFetchTasks } from '../../hooks/useFetchTaskList';
import CalendarView from './calendar';
import TaskCard from './taskCard';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);
import { IGroupedActivities, ITask } from '../../interface/tasks';
import { NoteEmpty } from '../icons';
import NoData from '../dashboard/noData';
import { useTranslation } from 'react-i18next';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { IFilterOption, ITransaction } from '../../interface/transaction';
import { taskFields, TasksFilterValues } from '../../constants/tasks';
import { useQueryClient } from '@tanstack/react-query';
import {
  calculateDuration,
  formatter,
  getDisplayKey,
  getStatusFilterForTasks,
} from '../../constants/utilityFunctions';
import { useUser } from '../../context/UserContext';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import { useTransaction } from '../../context/TransactionContext';
import { useTask } from '../../context/TaskContext';
import { Spin } from '../Ant';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useMarkActivityAsCompleted } from '../../hooks/useMarkActivityAsCompleted';
import CustomModal from '../informationPopup/customModal';
import { useScrollToDate } from '../../hooks/useScrollToDate';
import { useSearchParams } from 'react-router-dom';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonGroupedCardList from '../common/skeleton/skeletonGroupedCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../hooks/useFetchAllPages';

const GeneralTask = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const { updateTask, clearInitialTask } = useTask();
  const { getDateRef, scrollToDate } = useScrollToDate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('taskSearch') || '';
  const initialFilterValue = searchParams.get('taskFilter');
  const taskFilterOptions: IFilterOption[] = [
    { label: t('task:filter.allTasks'), value: TasksFilterValues.allTasks },
    { label: t('task:filter.completedTasks'), value: TasksFilterValues.completed },
    { label: t('task:filter.incompleteTasks'), value: TasksFilterValues.inComplete },
  ];
  const initialFilter = taskFilterOptions.find((opt) => opt.value === initialFilterValue);
  const initialSelectedDate = searchParams.get('taskSelectedDate');
  const initialStartDate = searchParams.get('taskStartDate');
  const initialEndDate = searchParams.get('taskEndDate');

  const parsedInitialStartDate = initialStartDate
    ? dayjs(initialStartDate.replace('$gte:', ''))
    : null;
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [selectedTask, setSelectedTask] = useState<ITask>();
  const filterDefaultValue = {
    label: t('task:filter.allTasks'),
    value: TasksFilterValues.allTasks,
  };
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption>(
    initialFilter || filterDefaultValue,
  );
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
    initialSelectedDate ? dayjs(initialSelectedDate) : null,
  );
  const [dateRange, setDateRange] = useState({
    endDate: initialStartDate || `$gte:${dayjs().startOf('isoWeek').toISOString()}`,
    startDate: initialEndDate || `$lte:${dayjs().endOf('isoWeek').toISOString()}`,
  });
  const [pendingMarkAsCompleteActivity, setPendingMarkAsCompleteActivity] = useState<ITask | null>(
    null,
  );
  const [markAsCompleteConfirmationModalVisible, setMarkAsCompleteConfirmationModalVisible] =
    useState<boolean>(false);
  const markAsCompletedMutation = useMarkActivityAsCompleted();
  const calendarActiveSort: IActiveSortState = {
    field: 'startTime',
    direction: 'ASC',
  };
  const calendarSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'startTime', label: t('task:startDate'), type: 'date', defaultDirection: 'ASC' },
    ],
    [t],
  );
  const {
    data: taskList,
    isLoading: isTasksLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useFetchTasks({
    pageSize: 50,
    relations: [
      'matter.parties',
      'matter.parties.company',
      'matter.parties.privatePerson',
      'matter:subject,matterNumber,currency,invoiceableType',
      'responsibleUser:fullName',
      'status',
      'activityType',
      'workTimes:duration',
    ],
    search: searchValue,
    extraFilters: {
      activityTypeId: `$eq:${SystemConstants.TASK_ID}`,
      startTime: dateRange?.startDate,
      dueTime: dateRange.endDate,
      statusId: getStatusFilterForTasks(selectedFilter?.value),
      responsibleUserId: `$eq:${user?.id}`,
    },
    fields: taskFields,
    enabled: true,
  });

  const initializeGroup = (displayKey: string, iterDate: dayjs.Dayjs): IGroupedActivities => ({
    displayKey,
    date: iterDate.toDate(),
    items: [],
    amount: 0,
    totalDuration: { value: '0', unit: 'minutes' },
  });

  const calculateTaskDuration = (item: ITask, iterDate: dayjs.Dayjs) => {
    const activityStart = dayjs.max(dayjs(item?.startTime), iterDate);
    const activityEnd = dayjs.min(dayjs(item?.dueTime), iterDate.endOf('day'));
    return calculateDuration(activityStart.toISOString(), activityEnd.toISOString());
  };

  const updateGroup = (
    group: IGroupedActivities,
    duration: { value: string; unit: 'minutes' | 'hours' },
    item: ITask,
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs,
    iterDate: dayjs.Dayjs,
  ): IGroupedActivities => {
    const newMinutes = convertToMinutes(duration);
    const existingMinutes = convertToMinutes(group.totalDuration);
    const totalMinutes = existingMinutes + newMinutes;

    group.totalDuration =
      totalMinutes < 60
        ? { value: totalMinutes.toString(), unit: 'minutes' }
        : {
            value: `${Math.floor(totalMinutes / 60)}${
              totalMinutes % 60 === 0 ? '' : `:${String(totalMinutes % 60).padStart(2, '0')}`
            }`,
            unit: 'hours',
          };

    group.amount += 1;

    const totalDays = endDate.diff(startDate, 'days') + 1;
    const currentDay = iterDate.diff(startDate, 'days') + 1;

    group.items.push({
      activity: item,
      currentOfTotalDays: totalDays > 1 ? `${currentDay}/${totalDays}` : '',
    });

    group.items.sort(
      (a: { activity: ITask }, b: { activity: ITask }) =>
        dayjs(a.activity.dueTime).valueOf() - dayjs(b.activity.dueTime).valueOf(),
    );

    return group;
  };

  const convertToMinutes = (duration: { value: string; unit: 'minutes' | 'hours' }): number => {
    if (duration.unit === 'hours') {
      const [h, m = 0] = duration.value.split(':').map(Number);
      return h * 60 + m;
    }
    return Number.parseInt(duration.value, 10);
  };
  const groupedMap = (taskList ?? []).reduce((acc, item): Record<string, IGroupedActivities> => {
    const startDate = dayjs(item?.startTime).startOf('day');
    const endDate = dayjs(item?.dueTime).startOf('day');
    const startOfWeekDayjs = dayjs(dateRange.endDate.replace('$gte:', '')).startOf('day');
    const endOfWeekDayjs = startOfWeekDayjs.add(6, 'day');
    if (endDate.isBefore(startOfWeekDayjs) || startDate.isAfter(endOfWeekDayjs)) {
      return acc; // Skip if out of week range
    }

    let iterDate = startDate;

    while (iterDate.isSameOrBefore(endDate)) {
      if (iterDate.isBefore(startOfWeekDayjs) || iterDate.isAfter(endOfWeekDayjs)) {
        iterDate = iterDate.add(1, 'day');
        continue;
      }

      const dateKey = iterDate.format('YYYY-MM-DD');
      const displayKey = getDisplayKey(dateKey, iterDate, t);

      acc[displayKey] ??= initializeGroup(displayKey, iterDate);

      const duration = calculateTaskDuration(item, iterDate);
      acc[displayKey] = updateGroup(acc[displayKey], duration, item, startDate, endDate, iterDate);

      iterDate = iterDate.add(1, 'day');
    }

    return acc;
  }, {});

  const sortedGrouped = (Object.values(groupedMap) as IGroupedActivities[]).sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  const { isCollapsed, toggle, expand, getToggleAllControl } = useCollapsedDateGroups(
    DateGroupList.tasks,
  );
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  const markAsComplete = (taskToComplete?: ITask) => {
    const activityToComplete = taskToComplete ?? pendingMarkAsCompleteActivity;
    if (!activityToComplete) return;
    markAsCompletedMutation.mutate(
      {
        id: activityToComplete?.id,
        version: activityToComplete?.version,
      },
      {
        onSettled() {
          setPendingMarkAsCompleteActivity(null);
          queryClient.removeQueries({ queryKey: ['tasks-list'] });
          refetch();
        },
      },
    );
  };
  const handleMarkAsComplete = (task?: ITask) => {
    if (task) {
      const createTransactionWhenMarkAsDone = task.activityType.createTransactionWhenMarkedAsDone;
      const workTimeInvoicedThroughActivity = task.activityType.workTimeInvoicedThroughActivity;
      const workTimesDurationMoreThan0 = task.workTimes?.some((workTime) => workTime.duration > 0);
      setPendingMarkAsCompleteActivity(task);
      if (
        !createTransactionWhenMarkAsDone &&
        workTimeInvoicedThroughActivity &&
        workTimesDurationMoreThan0 &&
        !task.transactionId
      ) {
        setMarkAsCompleteConfirmationModalVisible(true);
        return;
      }
      markAsComplete(task);
    }
  };
  const handleTaskToTransaction = (transaction?: ITransaction, taskData?: ITask) => {
    const sourceTask = taskData ?? selectedTask;
    if (transaction) {
      updateTransaction({
        matterId: sourceTask?.matterId,
        matter: sourceTask?.matter,
        subject: sourceTask?.subject,
        currentSubject: sourceTask?.subject,
        isManualUnitPrice: false,
        shouldLoadServerPrice: true,
        restrictPriceUpdate: true,
        principalCompany: sourceTask?.matter?.principalCompany,
        principalPrivatePerson: sourceTask?.matter?.principalPrivatePerson,
        overrideWarnings: true,
        activityId: sourceTask?.id,
        workedHours: sourceTask?.duration,
        billableQuantity: sourceTask?.duration,
      } as ITransaction);
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setSelectedTask(undefined);
      setSelectedTransaction(undefined);
    }
  };
  useEffect(() => {
    setSearchParams({
      taskSearch: searchValue,
      taskFilter: selectedFilter?.value || '',
      taskSelectedDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : '',
      taskStartDate: dateRange.endDate,
      taskEndDate: dateRange.startDate,
    });
  }, [searchValue, selectedFilter, selectedDate, dateRange]);
  return (
    <div className="d-flex flex-col h-full">
      <CollapsibleListLayout
        testId="general-task-listing"
        contentGap="gutter"
        contentClassName="w-full"
        pinnedHeader={
          <CalendarView
            onWeekChange={(start, end) => {
              setDateRange({
                endDate: `$gte:${dayjs(start).startOf('day').toISOString()}`,
                startDate: `$lte:${dayjs(end).endOf('day').toISOString()}`,
              });
            }}
            onDateChange={(dateObj) => {
              const dateKey = dayjs(dateObj).isSame(dayjs(), 'day')
                ? t('common:today')
                : dayjs(dateObj).format('MMM DD, YYYY');
              expand(toDateGroupKey(dateObj));
              scrollToDate(dateKey);
            }}
            onSelectedDateChange={(date) => {
              setSelectedDate(dayjs(date));
            }}
            groupedMap={groupedMap}
            initialWeekStart={parsedInitialStartDate ? parsedInitialStartDate.toDate() : undefined}
            customDate={initialSelectedDate ? new Date(initialSelectedDate) : undefined}
          />
        }
        header={
          <SearchInputWithFilters
            dateGroupToggle={getToggleAllControl(
              sortedGrouped.map((group) => toDateGroupKey(group.date)),
            )}
            searchTitle={t('task:search')}
            filterTitle={t('task:filter.taskFilters')}
            radioOptions={taskFilterOptions}
            searchValue={searchValue}
            handleSearch={(val: string) => {
              setSearchValue(val);
            }}
            handleRefresh={() => {
              queryClient.removeQueries({ queryKey: ['tasks-list'] });
              return refetch();
            }}
            selectedFilter={[selectedFilter]}
            setSelectedFilter={(val) => setSelectedFilter(val)}
            clearFilter={() => {
              setSelectedFilter(filterDefaultValue);
            }}
            handleAdd={() => {
              updateTask({
                startTime: selectedDate
                  ? dayjs(selectedDate).startOf('day').toISOString()
                  : undefined,
                dueTime: selectedDate ? dayjs(selectedDate).endOf('day').toISOString() : undefined,
              } as ITask);
              clearInitialTask();
              push(ROUTES.ADD_EDIT_TASK);
            }}
            tooltipTextForAdd={t('task:addNew')}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={calendarSortOptions}
              activeSort={calendarActiveSort}
              disabled
            />
          </SearchInputWithFilters>
        }
      >
        {sortedGrouped && Object.entries(sortedGrouped).length > 0 && (
          <>
            {sortedGrouped.map((val: IGroupedActivities) => {
              const formattedDate =
                val.displayKey === t('common:today')
                  ? val.displayKey
                  : formatter.dateStandard(new Date(val.displayKey));
              const groupKey = toDateGroupKey(val.date);
              return (
                <CollapsibleDateGroup
                  key={groupKey}
                  ref={getDateRef(val.displayKey)}
                  className="scroll-anchor"
                  title={formattedDate}
                  itemCount={t('task:taskCount', { count: val.items.length })}
                  collapsed={isCollapsed(groupKey)}
                  onToggle={() => toggle(groupKey)}
                >
                  {val.items.map((task: { activity: ITask; currentOfTotalDays: string }) => (
                    <TaskCard
                      key={task?.activity?.id}
                      task={task}
                      completeTask={handleMarkAsComplete}
                      handleRestrictions={(task: ITask, transaction: ITransaction) => {
                        setSelectedTask(task);
                        if (task?.matterId) {
                          setSelectedTransaction(transaction);
                        } else {
                          handleTaskToTransaction(
                            {
                              ...task,
                              matter: task?.matter,
                              matterId: task?.matterId,
                            } as unknown as ITransaction,
                            task,
                          );
                        }
                      }}
                    />
                  ))}
                </CollapsibleDateGroup>
              );
            })}
          </>
        )}
        {(!taskList || (Object.entries(taskList).length === 0 && !isTasksLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData header={t('task:noTasksForThisWeek')} subHeader="" icon={<NoteEmpty />} />
          </div>
        )}
        {isTasksLoading ? <SkeletonGroupedCardList rows={[1, 1]} /> : renderLoader()}
      </CollapsibleListLayout>
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleTaskToTransaction}
      />
      <CustomModal
        visible={markAsCompleteConfirmationModalVisible}
        title={t('task:markAsCompleteConfirmation')}
        cancelText={t('common:cancel')}
        confirmText={t('common:continue')}
        onConfirm={() => {
          markAsComplete();
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
        onClose={() => {
          setPendingMarkAsCompleteActivity(null);
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
      />
    </div>
  );
};

export default GeneralTask;
